import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { SELECTED_ITEMS, STATUS } from 'constants/formFields';
import {
  CATEGORY_IMPACT_TEMPLATE,
  KEY_PERMISSIONS,
  MATERIALITY_ASSESSMENT_STATUS,
  MODALS_IRO_IDENTIFICATION,
  TASK_MANAGEMENT_PERMISSIONS,
} from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import useGetCurrentStandards from 'hooks/useGetCurrentStandards';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks/useApp';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectStakeholderVcTab } from 'store/slices/general/selectors';
import { setStakeholderVcTab } from 'store/slices/general/slice';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useCreateCustomIROTemplatesMutation,
  useCreateCustomOpportunitiesTemplatesMutation,
  useCreateCustomRisksTemplatesMutation,
  useCreateRecipientUsersMutation,
  useDeleteCustomIROTemplatesMutation,
  useDeleteCustomOpportunitiesTemplatesMutation,
  useDeleteCustomRiskTemplatesMutation,
  useGetIROEmailsListQuery,
  useGetIROImpactListQuery,
  useGetIROOpportunitiesListQuery,
  useGetIRORisksListQuery,
  useGetIROTemplatesListQuery,
  useGetRecipientListQuery,
  useGetSingleMAInitiateQuery,
  useResendEmailMutation,
  useSelectedIROTemplatesMutation,
  useShareIroQuestionsMutation,
  useUpdateCustomIROTemplatesMutation,
  useUpdateCustomOpportunitiesTemplatesMutation,
  useUpdateCustomRiskTemplatesMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import { capitalizeFirstLetter } from '../../../utils/formatText';
import useTableCheckboxForm from './hooks/useTableCheckboxForm';
import MaterialAssessmentIROIndetificatorView from './MaterialAssessmentIROIndetificatorView';

import type IIROImpactItem from 'store/slices/materialAssessment/interfaces/IIROImpactItem';
import type IItemIROCustom from 'store/slices/materialAssessment/interfaces/IItemIROCustom';
import type IQuestionsSendItem from 'store/slices/materialAssessment/interfaces/IQuestionsSendItem';
import type IRecipientCreateRequest from 'store/slices/materialAssessment/interfaces/IRecipientCreateRequest';
import type IRespondentRequest from 'store/slices/materialAssessment/interfaces/IRespondentRequest';
import type ISendQuestions from 'store/slices/materialAssessment/interfaces/ISendQuestions';
import type ITemplateItem from 'store/slices/materialAssessment/interfaces/ITemplateItem';
import type IIRORecipientEmail from '../../../store/slices/materialAssessment/interfaces/IIRORecipientEmail';
import type ISelectedItem from './interfaces/ISelectedItem';
import type ISelectedTemplateItem from './interfaces/ISelectedTemplateItem';

function MaterialAssessmentIROIndetificator() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recordId = '' } = useParams();
  const [activeTab, setActiveTab] = useState(1);
  const stakeholderVcTab = useAppSelector(selectStakeholderVcTab);
  const [activeStandard, setActiveStandard] = useState(0);
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<ITemplateItem[]>([]);
  const [openModal, setOpenModal] = useState('');
  const [selectedItems, setSelectedItems] = useState<ISelectedItem[]>([]);
  const [offsetMain, setOffsetMain] = useState(0);
  const [itemsMain, setItemsMain] = useState<IIROImpactItem[]>([]);
  const [itemsEmailsList, setItemsEmailsList] = useState<IIRORecipientEmail[]>([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [selectEditItem, setSelectEditItem] = useState<IIROImpactItem | null>(null);

  const [respondentList, setRespondentList] = useState<IRespondentRequest[]>([]);
  const [respondentListOffset, setRespondentListOffset] = useState(0);
  const [respondentListKeyword, setRespondentListKeyword] = useState('');
  const [selectedRespondentItems, setSelectedRespondentItems] = useState<IRespondentRequest[]>([]);
  const [debouncedRespondentListKeyword, setDebouncedRespondentListKeyword] = useState('');
  const [selectedTemplateItems, setSelectedTemplateItems] = useState<ISelectedTemplateItem>(
    {
      1: [],
      2: [],
      3: [],
    },
  );
  const [updateSection] = useUpdateSectionMAMutation();

  const [createCustomIROTemplates] = useCreateCustomIROTemplatesMutation();
  const [updateCustomIROTemplates] = useUpdateCustomIROTemplatesMutation();
  const [deleteCustomIROTemplates] = useDeleteCustomIROTemplatesMutation();

  const [createCustomRisksTemplates] = useCreateCustomRisksTemplatesMutation();
  const [updateCustomRiskTemplates] = useUpdateCustomRiskTemplatesMutation();
  const [deleteCustomRiskTemplates] = useDeleteCustomRiskTemplatesMutation();

  const [createCustomOpportunitiesTemplates] = useCreateCustomOpportunitiesTemplatesMutation();
  const [updateCustomOpportunitiesTemplates] = useUpdateCustomOpportunitiesTemplatesMutation();
  const [deleteCustomOpportunitiesTemplates] = useDeleteCustomOpportunitiesTemplatesMutation();

  const [resendEmail] = useResendEmailMutation();
  const [shareIroQuestions] = useShareIroQuestionsMutation();
  const [createRecipientUsers] = useCreateRecipientUsersMutation();

  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: naceCodeTreeData = { data: {} }, isLoading: isGetNaceCodeTreeRecords } = useGetNaceCodeTreeQuery();

  const [selectIROTemplates] = useSelectedIROTemplatesMutation();

  const { standards, isFetchingStandards } = useGetCurrentStandards();
  const {
    control,
    setValue,
    watch,
  } = useTableCheckboxForm();

  const buildQuestionsItems = (ids?: number[]): IQuestionsSendItem[] => {
    if (!ids || ids.length === 0) {
      return [];
    }

    if (selectedRespondentItems.length === 0) {
      return [];
    }

    return ids.flatMap((questionId) => selectedRespondentItems.map((respondent) => ({
      id: questionId,
      email: respondent.email,
      respondentType: respondent.respondentType,
    })));
  };

  const statusFilter = watch(STATUS);
  const iroTemplateCategoriesByTab: Record<number, string[]> = {
    1: [CATEGORY_IMPACT_TEMPLATE.IMPACT],
    2: [CATEGORY_IMPACT_TEMPLATE.RISK],
    3: [CATEGORY_IMPACT_TEMPLATE.OPPORTUNITY],
  };
  const selectedTemplateCategories = iroTemplateCategoriesByTab[activeTab] || [];

  const handleGetCode = () => {
    const selectedLabels = statusFilter?.filter((item): item is string => item !== undefined);
    const selectedItemsCode = Object.keys(naceCodeTreeData.data)
      .filter((key) => selectedLabels?.includes(naceCodeTreeData.data[key].label));
    return selectedItemsCode || [];
  };

  const {
    data: mainImpacts,
    isFetching: isLoadingMainImpacts,
    refetch: refetchMainImpacts,
  } = useGetIROImpactListQuery({
    maId: recordId,
    offset: activeTab === 1 ? offsetMain : 0,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === 0,
  });

  const {
    data: risks,
    isFetching: isLoadingRisks,
    refetch: refetchRisks,
  } = useGetIRORisksListQuery({
    maId: recordId,
    offset: activeTab === 2 ? offsetMain : 0,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === 0,
  });

  const {
    data: opportunities,
    isFetching: isLoadingOpportunities,
    refetch: refetchOpportunities,
  } = useGetIROOpportunitiesListQuery({
    maId: recordId,
    offset: activeTab === 3 ? offsetMain : 0,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === 0,
  });

  const { data: templates, isFetching: isLoadingTemplates } = useGetIROTemplatesListQuery({
    offset,
    standardId: activeStandard,
    naceSections: handleGetCode(),
    categories: selectedTemplateCategories,
  });

  const {
    data: emailsList,
    isFetching: isLoadingEmailsList,
  } = useGetIROEmailsListQuery({
    maId: recordId,
    offset: activeTab === 4 ? offsetMain : 0,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === 0,
  });

  const { data: userData, isFetching: isLoadingUserMe } = useUserMeQuery();
  const companyId = userData?.data?.companyId;
  const { access } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const { data: recipientList, isFetching: isLoadingRecipientList } = useGetRecipientListQuery({
    companyId: companyId || 0,
    offset: respondentListOffset,
    keyword: debouncedRespondentListKeyword,
  }, { skip: !companyId });

  const hasMoreRespondent = recipientList?.data?.meta?.hasNext || false;

  const onLoadMoreRespondent = () => {
    if (hasMoreRespondent) {
      setRespondentListOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (recipientList?.data?.items) {
      if (respondentListOffset === 0) {
        setRespondentList(recipientList.data.items);
      } else {
        setRespondentList((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = recipientList.data.items.filter((item) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [recipientList, respondentListOffset]);

  const debouncedChangeRespondentListKeyword = useDebouncedCallback((text: string) => {
    setDebouncedRespondentListKeyword(text);
    setRespondentListOffset(0);
  }, 500);

  const handleChangeRespondentListKeyword = (text: string) => {
    setRespondentListKeyword(text);
    debouncedChangeRespondentListKeyword(text);
  };

  const handleSelectRespondentItem = (item: IRespondentRequest | null) => {
    if (!item) {
      setSelectedRespondentItems([]);
      return;
    }

    setSelectedRespondentItems((prev) => {
      const respondentIndex = prev.findIndex((respondent) => respondent.email === item.email);
      if (respondentIndex === -1) {
        return [...prev, item];
      }
      const existingRespondent = prev[respondentIndex];
      if (item.respondentType !== undefined && item.respondentType !== existingRespondent.respondentType) {
        const updatedRespondents = [...prev];
        updatedRespondents[respondentIndex] = {
          ...existingRespondent,
          respondentType: item.respondentType,
        };
        return updatedRespondents;
      }
      return prev.filter((respondent) => respondent.email !== item.email);
    });
  };

  const hasMore = templates?.data?.meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const hasMoreMainByTab: Record<number, boolean> = {
    1: mainImpacts?.data?.meta?.hasNext || false,
    2: risks?.data?.meta?.hasNext || false,
    3: opportunities?.data?.meta?.hasNext || false,
    4: emailsList?.data?.meta?.hasNext || false,
  };
  const hasMoreMain = hasMoreMainByTab[activeTab] || false;

  const onLoadMoreMain = () => {
    if (hasMoreMain) {
      setOffsetMain((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const handleRefreshMainList = (tabId: number) => {
    setItemsMain([]);

    if (offsetMain !== 0) {
      setOffsetMain(0);
      return;
    }

    if (tabId === 1) {
      refetchMainImpacts();
      return;
    }

    if (tabId === 2) {
      refetchRisks();
      return;
    }

    if (tabId === 3) {
      refetchOpportunities();
    }
  };

  useEffect(() => {
    if (!isViewOnly && stakeholderVcTab) {
      const checkImpacts = (mainImpacts?.data?.items?.length ?? 0) > 0;
      const checkRisks = (risks?.data?.items?.length ?? 0) > 0;
      const checkOpportunities = (opportunities?.data?.items?.length ?? 0) > 0;
      const checkEmails = (emailsList?.data?.items?.length ?? 0) > 0;
      updateSection({
        maId: recordId,
        type: SECTION_TYPE.IRO_IDENTIFICATION_VCM,
        valueChainMapId: stakeholderVcTab,
        data: {
          isActive: true,
          status: checkImpacts
          && checkRisks
          && checkOpportunities
          && checkEmails ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS,
        },
      });
    }
  }, [isViewOnly, recordId, updateSection, stakeholderVcTab,
    mainImpacts, risks, opportunities, emailsList]);

  useEffect(() => {
    if (templates?.data.items) {
      if (offset === 0) {
        setItems(templates.data.items);
      } else {
        setItems((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = templates.data.items.filter((item) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [templates, offset, activeTab]);

  useEffect(() => {
    if (activeTab === 1) {
      if (mainImpacts?.data.items) {
        if (offsetMain === 0) {
          setItemsMain(mainImpacts.data.items);
        } else {
          setItemsMain((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = mainImpacts.data.items.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }

    if (activeTab === 2) {
      if (risks?.data.items) {
        if (offsetMain === 0) {
          setItemsMain(risks.data.items);
        } else {
          setItemsMain((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = risks.data.items.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }

    if (activeTab === 3) {
      if (opportunities?.data.items) {
        if (offsetMain === 0) {
          setItemsMain(opportunities.data.items);
        } else {
          setItemsMain((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = opportunities.data.items.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }

    if (activeTab === 4) {
      if (emailsList?.data.items) {
        if (offsetMain === 0) {
          setItemsEmailsList(emailsList.data.items);
        } else {
          setItemsEmailsList((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const newItems = emailsList.data.items.filter((item) => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
        }
      }
    }
  }, [mainImpacts, offsetMain, activeTab, risks, opportunities, emailsList?.data.items]);

  useEffect(() => {
    if (standards && standards.data.items.length > 0) {
      setActiveStandard(standards.data.items[0].id);
    }
  }, [standards]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter]);

  const handleGoBack = () => {
    navigate(AppRoutes.materialityAssessmentTopicRefinementII.replace(':recordId', recordId || ''));
  };

  const handleNext = () => {
    navigate(AppRoutes.materialityAssessmentIRORefinement.replace(':recordId', recordId));
  };

  const handleCancel = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const handleClickTab = (tabId: number) => {
    setActiveTab(tabId);
    setOffset(0);
    setOffsetMain(0);
    setValue(STATUS, []);
  };

  const handleClickVcTab = (vcId: number) => {
    dispatch(setStakeholderVcTab(vcId));
    setOffset(0);
    setOffsetMain(0);
    setValue(STATUS, []);
  };

  const handleActiveStandard = (standardId: number) => {
    if (standardId === activeStandard) return;
    setActiveStandard(standardId);
    setOffset(0);
    setItems([]);
  };

  const handleAddNewImpact = () => {
    setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_NEW_ITEM);
  };

  const handleAddEmail = () => {
    setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_NEW_EMAIL);
  };

  const handleSendEmails = () => {
    handleClickTab(1);
    setOpenModal(MODALS_IRO_IDENTIFICATION.SEND_EMAILS);
  };

  const handleUnSelectAll = () => {
    setSelectedItems([]);
    setValue(SELECTED_ITEMS, []);
  };

  const handleCloseModal = () => {
    setOpenModal('');
    setSelectedItem(null);
    setOpenDeleteModal(false);
    setSelectedItems([]);
    handleUnSelectAll();
    handleSelectRespondentItem(null);
    setSelectedTemplateItems({
      1: [],
      2: [],
      3: [],
    });
    setSelectEditItem(null);
  };

  const handleAddSubmit = async () => {
    const selectedIds = selectedItems.map((item) => item.id);

    try {
      if (!selectedTemplateCategories.length) {
        return;
      }

      await selectIROTemplates({
        maId: recordId,
        vcmId: stakeholderVcTab || 0,
        data: {
          ids: selectedIds,
        },
      }).unwrap();
      handleRefreshMainList(activeTab);

      toast.success(i18n.t('notifications.addedSelectedTemplates'));
      handleUnSelectAll();
      setOpenModal('');
      setValue(STATUS, []);
    } catch {
      toast.error(i18n.t('notifications.errorAddSelectedTemplates'));
    }
  };

  const handleCreateCustomItem = () => {
    if (activeTab === 1) {
      setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_IMPACT);
    }
    if (activeTab === 2) {
      setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_RISKS);
    }
    if (activeTab === 3) {
      setOpenModal(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY);
    }
  };

  const onSelectItem = ({ id, isSelected }: ISelectedItem) => {
    setSelectedItems((prev) => {
      const itemIndex = prev.findIndex((item) => item.id === id);
      if (itemIndex > -1) {
        if (!isSelected) {
          return prev.filter((item) => item.id !== id);
        }
        const updatedItems = [...prev];
        updatedItems[itemIndex].isSelected = isSelected;
        return updatedItems;
      }
      return isSelected ? [...prev, { id, isSelected }] : prev;
    });
  };

  const createIROItem = async (data: IItemIROCustom) => {
    if (activeTab === 1) {
      try {
        await createCustomIROTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
        }).unwrap();
        handleRefreshMainList(1);
        toast.success(i18n.t('notifications.createdIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
        }));
      } catch {
        toast.error(i18n.t('notifications.errorCreateIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
        }));
      }
    } else if (activeTab === 2) {
      try {
        await createCustomRisksTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
        }).unwrap();
        handleRefreshMainList(2);
        toast.success(i18n.t('notifications.createdIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
        }));
      } catch {
        toast.error(i18n.t('notifications.errorCreateIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
        }));
      }
    } else if (activeTab === 3) {
      try {
        await createCustomOpportunitiesTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
        }).unwrap();
        handleRefreshMainList(3);
        toast.success(i18n.t('notifications.createdIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY),
        }));
      } catch {
        toast.error(i18n.t(
          'notifications.errorCreateIROItem',
          {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY),
          },
        ));
      }
    }
  };

  const updateIROItem = async (data: IItemIROCustom) => {
    if (activeTab === 1) {
      try {
        await updateCustomIROTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
          id: selectEditItem?.id,
        }).unwrap();
        handleRefreshMainList(1);
        toast.success(i18n.t('notifications.updatedIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
        }));
      } catch {
        toast.error(i18n.t('notifications.errorUpdateIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
        }));
      }
    } else if (activeTab === 2) {
      try {
        await updateCustomRiskTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
          id: selectEditItem?.id,
        }).unwrap();
        handleRefreshMainList(2);
        toast.success(i18n.t('notifications.updatedIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
        }));
      } catch {
        toast.error(i18n.t('notifications.errorUpdateIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
        }));
      }
    } else if (activeTab === 3) {
      try {
        await updateCustomOpportunitiesTemplates({
          maId: recordId,
          vcmId: stakeholderVcTab || 0,
          data,
          id: selectEditItem?.id,
        }).unwrap();
        handleRefreshMainList(3);
        toast.success(i18n.t('notifications.updatedIROItem', {
          itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY),
        }));
      } catch {
        toast.error(i18n.t(
          'notifications.errorUpdateIROItem',
          { itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY) },
        ));
      }
    }
  };

  const deleteTemplate = (id: number) => {
    setSelectedItem(id);
    setOpenDeleteModal(true);
  };

  const onConfirm = () => {
    if (selectedItem) {
      if (activeTab === 1) {
        deleteCustomIROTemplates({ maId: recordId, id: selectedItem, vcmId: stakeholderVcTab || 0 }).then(() => {
          refetchMainImpacts();
          toast.success(i18n.t('notifications.deleteIROItem', {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
          }));
        }).catch(() => {
          toast.error(i18n.t('notifications.errorDeleteIROItem', {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_IMPACT),
          }));
        });
      } else if (activeTab === 2) {
        deleteCustomRiskTemplates({ maId: recordId, id: selectedItem, vcmId: stakeholderVcTab || 0 }).then(() => {
          refetchRisks();
          toast.success(i18n.t('notifications.deleteIROItem', {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
          }));
        }).catch(() => {
          toast.error(i18n.t('notifications.errorDeleteIROItem', {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_RISKS),
          }));
        });
      } else if (activeTab === 3) {
        deleteCustomOpportunitiesTemplates({
          maId: recordId,
          id: selectedItem,
          vcmId: stakeholderVcTab || 0,
        }).then(() => {
          refetchOpportunities();
          toast.success(i18n.t('notifications.deleteIROItem', {
            itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY),
          }));
        }).catch(() => {
          toast.error(i18n.t(
            'notifications.errorDeleteIROItem',
            { itemName: capitalizeFirstLetter(MODALS_IRO_IDENTIFICATION.ADD_OPPORTUNITY) },
          ));
        });
      }
      setItemsMain((prev) => prev.filter((item) => item.id !== selectedItem));
    }
    handleCloseModal();
  };

  const handleEdit = (id: number) => {
    const editItem = itemsMain.find((item) => item.id === id) || null;
    if (editItem) {
      setSelectEditItem(editItem);
      handleCreateCustomItem();
    }
  };

  const handleResend = (id: number) => {
    if (id) {
      resendEmail({
        maId: Number(+recordId),
        questionnaireRecipientId: id,
      })
        .unwrap()
        .then(() => {
          toast.success(i18n.t('notifications.resendIROEmail'));
        }).catch((e) => {
          if (e?.data?.errorCode === '2.47.7') {
            toast.error(i18n.t('notifications.errorCooldownResendEmail'));
          } else {
            toast.error(i18n.t('notifications.errorResendEmail'));
          }
        });
    }
  };

  const handleSelectTemplateItem = (tabId: number, itemId: number) => {
    setSelectedTemplateItems((prev) => {
      const currentArray = prev?.[tabId as keyof ISelectedTemplateItem] || [];
      const exists = currentArray.includes(itemId);
      return {
        ...prev,
        [tabId as keyof ISelectedTemplateItem]: exists
          ? currentArray.filter((id) => id !== itemId)
          : [...currentArray, itemId],
      };
    });
  };

  const handleSendQuestionary = async () => {
    const impactsIds = selectedTemplateItems[1];
    const risksIds = selectedTemplateItems[2] || [];
    const opportunitiesIds = selectedTemplateItems[3] || [];

    if (selectedRespondentItems.length === 0) {
      return;
    }

    const body: ISendQuestions = {
      maId: Number(recordId),
      valueChainMaps: [{
        vcmId: stakeholderVcTab || 0,
        impacts: buildQuestionsItems(impactsIds),
        risks: buildQuestionsItems(risksIds),
        opportunities: buildQuestionsItems(opportunitiesIds),
      }],
    };

    try {
      await shareIroQuestions(body).unwrap();
      toast.success(i18n.t('notifications.sentQuestionary'));
      setActiveTab(5);
      handleCloseModal();
    } catch {
      toast.error(i18n.t('notifications.errorSendQuestionary'));
    }
  };

  useEffect(() => {
    if (singleMA && singleMA?.data?.valueChainMaps && singleMA?.data?.valueChainMaps?.length > 0
      && stakeholderVcTab === null) {
      dispatch(setStakeholderVcTab(singleMA.data.valueChainMaps[0].id));
    }
  }, [singleMA, dispatch, stakeholderVcTab]);

  const handleCreateRecipientUsers = async (data: IRecipientCreateRequest) => {
    await createRecipientUsers(data).unwrap();
    toast.success(i18n.t('notifications.createdRecipientUsers'));
  };

  useEffect(() => {
    setRespondentListKeyword('');
    setDebouncedRespondentListKeyword('');
  }, [stakeholderVcTab]);

  const loading = isLoadingTemplates || isGetNaceCodeTreeRecords || isFetchingStandards || isLoadingMainImpacts
  || isLoadingRisks || isLoadingOpportunities || isLoadingEmailsList || isLoadingSingleMA
  || isLoadingUserMe;

  return (
    <MaterialAssessmentIROIndetificatorView
      handleGoBack={handleGoBack}
      activeTab={activeTab}
      handleClickTab={handleClickTab}
      items={items}
      handleAddNewImpact={handleAddNewImpact}
      openModal={openModal}
      handleCloseModal={handleCloseModal}
      handleAddSubmit={handleAddSubmit}
      handleCreateCustomItem={handleCreateCustomItem}
      control={control}
      hasMore={hasMore}
      loading={loading}
      onLoadMore={onLoadMore}
      naceCodeTreeData={naceCodeTreeData}
      onSelectItem={onSelectItem}
      selectedItems={selectedItems}
      handleUnSelectAll={handleUnSelectAll}
      standards={standards?.data.items || []}
      activeStandard={activeStandard}
      handleActiveStandard={handleActiveStandard}
      hasMoreMain={hasMoreMain}
      onLoadMoreMain={onLoadMoreMain}
      itemsMain={itemsMain}
      itemsEmailsList={itemsEmailsList}
      createIROItem={createIROItem}
      deleteTemplate={deleteTemplate}
      isDeleteModalOpen={openDeleteModal}
      onDeleteConfirm={onConfirm}
      handleEdit={handleEdit}
      selectEditItem={selectEditItem}
      updateIROItem={updateIROItem}
      handleNext={handleNext}
      handleResend={handleResend}
      handleCancel={handleCancel}
      mainVcTabs={singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ value: itemVC.name, id: itemVC.id, name: itemVC.name }),
      ) || []}
      handleClickVcTab={handleClickVcTab}
      activeVcTab={stakeholderVcTab || 0}
      respondentList={respondentList}
      onLoadMoreRespondent={onLoadMoreRespondent}
      hasMoreRespondent={hasMoreRespondent}
      handleChangeRespondentListKeyword={handleChangeRespondentListKeyword}
      respondentListKeyword={respondentListKeyword}
      handleAddEmail={handleAddEmail}
      handleSendEmails={handleSendEmails}
      companyId={companyId || 0}
      selectedRespondentItems={selectedRespondentItems}
      handleSelectRespondentItem={handleSelectRespondentItem}
      handleSendQuestionary={handleSendQuestionary}
      handleSelectTemplateItem={handleSelectTemplateItem}
      handleCreateRecipientUsers={handleCreateRecipientUsers}
      isLoadingRecipientList={isLoadingRecipientList}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialAssessmentIROIndetificator;
